import { useEffect, useState } from 'react';
import type { BannerCard, BannerKind, BannerStatus, Folder } from '../../shared/types';
import { formatKey, formatName } from '../../shared/formats';
import { motifKey, motifName } from '../../shared/motif';

export type GroupBy = 'folder' | 'format' | 'motif';
export type StatusFilter = 'all' | BannerStatus;

export interface BannerGroup {
  key: string;
  title: string;
  subtitle: string;
  /** Nur bei Gruppierung nach Ordner: der Ordner (null = ohne Ordner). */
  folder?: Folder | null;
  banners: BannerCard[];
}

export const GROUP_LABELS: Record<GroupBy, string> = { folder: 'Ordner', format: 'Format', motif: 'Motiv' };
export const KIND_ORDER: BannerKind[] = ['html5', 'gif', 'image', 'video'];

const area = (b: BannerCard) => (b.width ?? 0) * (b.height ?? 0);

/** Motive nach Ordner, Format oder Motiv gruppieren – in der Reihenfolge der Kampagne. */
export function groupBanners(
  banners: BannerCard[],
  folders: Folder[],
  by: GroupBy,
  { keepEmptyFolders = false } = {},
): BannerGroup[] {
  if (by === 'folder') {
    const groups: BannerGroup[] = folders.map((folder) => ({
      key: folder.id,
      title: folder.name,
      subtitle: '',
      folder,
      banners: banners.filter((b) => b.folderId === folder.id),
    }));
    const loose = banners.filter((b) => !b.folderId || !folders.some((f) => f.id === b.folderId));
    if (loose.length > 0 || folders.length === 0) {
      groups.push({
        key: 'none',
        title: folders.length ? 'Ohne Ordner' : 'Alle Motive',
        subtitle: '',
        folder: null,
        banners: loose,
      });
    }
    return groups.filter((g) => g.banners.length > 0 || (keepEmptyFolders && g.folder));
  }

  const groups = new Map<string, BannerGroup>();
  for (const banner of banners) {
    let key: string;
    let title: string;
    let subtitle = '';
    if (by === 'format') {
      key = formatKey(banner.width, banner.height);
      title = formatName(banner.width, banner.height);
      subtitle = banner.width && banner.height ? `${banner.width} × ${banner.height} px` : '';
    } else {
      // Heißt ein Motiv nur „300x250“, gibt der Ordner den Motivnamen vor (Pakete je Motiv-Ordner).
      const folder = folders.find((f) => f.id === banner.folderId);
      const name = motifName(banner.name);
      key = name ? `m:${motifKey(banner.name)}` : folder ? `f:${folder.id}` : 'none';
      title = name || folder?.name || 'Ohne Motivnamen';
    }
    if (!groups.has(key)) groups.set(key, { key, title, subtitle, banners: [] });
    groups.get(key)!.banners.push(banner);
  }
  const list = [...groups.values()];
  if (by === 'motif') {
    for (const g of list) {
      // Innerhalb eines Motivs die Formate von groß nach klein, damit Sets gleich aufgebaut sind.
      g.banners.sort((a, b) => area(b) - area(a));
      g.subtitle = `${g.banners.length} ${g.banners.length === 1 ? 'Format' : 'Formate'}`;
    }
  }
  return list;
}

export function filterBanners(
  banners: BannerCard[],
  hidden: BannerKind[],
  status: StatusFilter,
): BannerCard[] {
  return banners.filter((b) => !hidden.includes(b.kind) && (status === 'all' || b.status === status));
}

/** Ansicht merken (je Person im Browser) – private Fenster dürfen das ablehnen. */
export function useStoredState<T extends string>(key: string, fallback: T, allowed: readonly T[]) {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key) as T | null;
      return stored && allowed.includes(stored) ? stored : fallback;
    } catch {
      return fallback;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // ohne Speicher gilt die Wahl nur für diesen Besuch
    }
  }, [key, value]);
  return [value, setValue] as const;
}
